import { Suspense, useState } from 'react'
import { Outlet, useOutletContext, useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { ProjectManagementProjectDetailsPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { ProjectLog } from 'modules/project_management/components/ProjectLog'

const ProjectDetailsPage = () => {
  const [openLog, setOpenLog] = useState(false)
  const { company } = useCurrentSession()
  const { hasBudgetFeature, hasCostsFeature, hasExpenses, hasPayingProjectsFeature } =
    useOutletContext<{
      hasBudgetFeature: boolean
      hasCostsFeature: boolean
      hasExpenses: boolean
      hasPayingProjectsFeature: boolean
    }>()

  useEffect(() => {
    track(trackingEvents.projectManagement.projects.goToProjectDetails)
  }, [])

  const { id } = useParams()

  const { data, loading, error } = useQuery(ProjectManagementProjectDetailsPageDocument, {
    variables: {
      companyId: company.id,
      featureIds: [
        Features.projects_overview,
        Features.projects_tasks,
        Features.projects_flexible_tracking,
        Features.sales_invoices_creation,
      ],
      hasSpendings: hasCostsFeature && hasExpenses,
      hasCostPlanning: hasCostsFeature && hasBudgetFeature,
      hasFixedTimeBudget: hasBudgetFeature,
      projectId: Number(id),
    },
  })

  const project = data ? data.projectManagement.projectsConnection.nodes[0] : null

  const contextlessPolicies = data ? data.projectManagement._policies : null
  const attendanceContextlessPolicies = data ? data.attendance._policies : null

  const hasProjectTasks = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_tasks
  )?.available
  const hasOverviewEnabled = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_overview
  )?.available
  const hasFlexibleTracking = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_flexible_tracking
  )?.available

  const salesInvoicesUsage = data?.companies?.featureAvailabilitiesConnection?.nodes.find(
    (feature) => feature.id === Features.sales_invoices_creation
  )

  const invoicesUsage = salesInvoicesUsage?.usage || 0
  const invoicesLimit = salesInvoicesUsage?.usageLimit || -1

  const { navigation } = useNavigation()

  const navigate = useNavigate()

  const handleOnSettingsClick = () => {
    track(trackingEvents.projectManagement.settings.visitProjectSettings, {
      origin: 'Project details page',
    })
    navigate(navigation.projects.id.settings.fullPath({ id: Number(id) }))
  }

  const handleOnLogClick = () => {
    track(trackingEvents.projectManagement.settings.viewLog, {
      origin: 'Project details page',
    })

    setOpenLog(true)
  }

  const isSaleInvoiceDetail = location.pathname.includes('/sale_invoices/detail/')

  return (
    <ResponsivePage.Body
      showHeader={false}
      variant={isSaleInvoiceDetail ? 'fullscreen' : 'wide'}
      loading={loading}
      error={error}
      authorized={!!project}
      widget={
        <Box alignItems="flexEnd" flexDirection="rowReverse" gap="s8">
          <Button onClick={handleOnSettingsClick} type="secondary" iconName="settings" />
          <Button onClick={handleOnLogClick} type="secondary" iconName="history" />
        </Box>
      }
    >
      <>
        <Suspense>
          <Outlet
            context={{
              attendanceContextlessPolicies,
              contextlessPolicies,
              project,
              hasBudgetFeature,
              hasCost: hasCostsFeature,
              hasExpenses,
              hasFlexibleTracking,
              hasProjectTasks,
              hasOverviewEnabled,
              invoicesUsage,
              invoicesLimit,
              hasPayingProjectsFeature,
            }}
          />
        </Suspense>

        {project && openLog && (
          <ProjectLog
            id={project.id}
            name={project.name}
            onClose={() => {
              setOpenLog(false)
            }}
          />
        )}
      </>
    </ResponsivePage.Body>
  )
}

export default ProjectDetailsPage
